// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Reflect Overlay Component
// ═══════════════════════════════════════════════════════════════════════════
// Full-screen overlay with Meditate and Journal options
// ═══════════════════════════════════════════════════════════════════════════

import { icons } from './icons.js';
import { showMeditationMode } from './MeditationMode.js';
import { showJournalEditor } from './JournalEditor.js';
import { isAuthenticated } from '../services/auth.js';
import { showAuthPrompt } from './AuthPrompt.js';

let currentOverlay = null;
let currentVerse = null;

export function showReflectOverlay(verse) {
  if (currentOverlay) return;

  currentVerse = verse;

  const overlay = document.createElement('div');
  overlay.className = 'reflect-overlay';
  overlay.id = 'reflect-overlay';

  overlay.innerHTML = `
    <div class="reflect-panel">
      <div class="reflect-handle"></div>
      
      <div class="reflect-header">
        <h2 class="reflect-title">Reflect</h2>
        <button class="reflect-close-btn" id="reflect-close" aria-label="Close">
          ${icons.x}
        </button>
      </div>
      
      <div class="reflect-verse-preview">
        <span class="reflect-verse-ref">${verse.reference}</span>
        <p class="reflect-verse-text">${verse.text.length > 120 ? verse.text.slice(0, 120) + '...' : verse.text}</p>
      </div>
      
      <div class="reflect-options">
        <button class="reflect-option meditate-option" id="reflect-meditate">
          <div class="reflect-option-icon">
            ${icons.meditation}
          </div>
          <div class="reflect-option-content">
            <h3 class="reflect-option-title">Meditate</h3>
            <p class="reflect-option-desc">Breathe deeply and soak in this verse</p>
          </div>
          <div class="reflect-option-arrow">
            ${icons.chevronDown}
          </div>
        </button>
        
        <button class="reflect-option journal-option" id="reflect-journal">
          <div class="reflect-option-icon">
            ${icons.journal}
          </div>
          <div class="reflect-option-content">
            <h3 class="reflect-option-title">Journal</h3>
            <p class="reflect-option-desc">Write your thoughts and reflections</p>
          </div>
          <div class="reflect-option-arrow">
            ${icons.chevronDown}
          </div>
        </button>
      </div>
      
      <button class="reflect-cancel-btn" id="reflect-cancel">Cancel</button>
    </div>
  `;

  document.body.appendChild(overlay);
  currentOverlay = overlay;

  // Animate in
  requestAnimationFrame(() => {
    overlay.classList.add('active');
  });

  // Setup handlers
  setupReflectHandlers(overlay);
}

function setupReflectHandlers(overlay) {
  const closeBtn = overlay.querySelector('#reflect-close');
  const cancelBtn = overlay.querySelector('#reflect-cancel');
  const meditateBtn = overlay.querySelector('#reflect-meditate');
  const journalBtn = overlay.querySelector('#reflect-journal');

  // Close button
  closeBtn.addEventListener('click', hideReflectOverlay);

  // Cancel button
  cancelBtn.addEventListener('click', hideReflectOverlay);

  // Click outside to close
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      hideReflectOverlay();
    }
  });

  // ESC key
  const handleEsc = (e) => {
    if (e.key === 'Escape') {
      hideReflectOverlay();
      document.removeEventListener('keydown', handleEsc);
    }
  };
  document.addEventListener('keydown', handleEsc);

  // Meditate option
  meditateBtn.addEventListener('click', () => {
    hideReflectOverlay();
    showMeditationMode(currentVerse);
  });

  // Journal option
  journalBtn.addEventListener('click', () => {
    hideReflectOverlay();

    // Journal requires auth
    if (!isAuthenticated()) {
      showAuthPrompt(() => {
        showJournalEditor(currentVerse);
      }, 'journal');
      return;
    }

    showJournalEditor(currentVerse);
  });
}

export function hideReflectOverlay() {
  if (!currentOverlay) return;

  currentOverlay.classList.remove('active');
  setTimeout(() => {
    currentOverlay?.remove();
    currentOverlay = null;
    currentVerse = null;
  }, 300);
}

export default { showReflectOverlay, hideReflectOverlay };
